import React, {useMemo, useState} from 'react';
import projectUtils from "../../../../../utils/project-utils.js";
import {differenceInYears, isValid} from "date-fns";
import {getAssetPath} from "../../../../../utils/get-asset-path.js";
import {navigateHook} from "../../../../../hooks/use-navigate.js";
import IndividualGridPagination, {
    getIndividualDefaultPageSize,
    getIndividualPaginatedRowData
} from "./individual-grid-pagination.jsx";
import {useQuery} from "@tanstack/react-query";
import bosService from "../../../../../services/web/bos-service.js";
import IndividualPhoto from "./grid-renderers/face-renderer.jsx";

const API_KEEPER = import.meta.env.VITE_API_KEEPER;

const situationClass = {
    PROCURADO: "bg-[#ef7d00]",
    FORAGIDO: "bg-[#b10919]",
    SOLTO: "bg-[#1b6ea2]",
    MORTO: "bg-[#000]",
    DETIDO: "bg-[#343a40]",
    LIBERADO: "bg-[#343a40]",
    QUALIFICADO: "bg-[#343a40]",
}

const individualJsonCache = new WeakMap();

const getIndividualJsonData = (individual) => {
    if (!individual?.data) return individual ?? {};

    const cached = individualJsonCache.get(individual);
    const rawData = typeof individual.data === "object" ? individual.data.value : individual.data;

    if (cached?.data === rawData) return cached.value;

    try {
        const value = typeof rawData === "string" ? JSON.parse(rawData) : rawData;
        individualJsonCache.set(individual, {data: rawData, value: value ?? {}});

        return value ?? {};
    } catch {
        return {};
    }
}

const formatBirth = (individual) => {
    if (!individual.dtBirth) return "";

    const birthDate = new Date(individual.dtBirth);
    if (!isValid(birthDate)) return individual.dtBirth;

    const age = differenceInYears(new Date(), birthDate)

    return `${projectUtils.formatDate(individual.dtBirth,  "DD/MM/YYYY")} (${age} anos)`;
}

const formatAddress = (individual) => {
    if (individual.homeless === true) return "[Situação de rua]";
    if (!Array.isArray(individual.addresses)) return "";

    const addresses = [...individual.addresses].sort((a, b) => new Date(a.date) - new Date(b.date));
    const address = addresses[addresses.length - 1];
    if (!address) return "";

    const addressText = [
        address.address,
        address.number,
        address.neighborhood,
        address.complement,
        address.city,
    ].filter(Boolean).join(", ");

    if (!addressText) return "";

    return address.type ? `${addressText} (${address.type})` : addressText;
}

const getAttachUrl = (attachId) => `${API_KEEPER}/ged/attach/${attachId}`;

const openIndividual = (cpf, navigate) => {
    if (!cpf) return;

    const urlDev = `/individuals/form/${cpf}`
        navigate(urlDev, true);
}

const IndividualSituation = ({situation}) => {
    if (!situation) return null;

    return (
        <div className={`absolute top-2.5 left-5 mt-2 rounded px-2 py-1 text-center text-xs font-bold text-white ${situationClass[situation]}`}>
            {situation}
        </div>
    );
}

const IndividualAttachs = ({individual}) => {
    const attachs = (individual.attachs ?? []).filter(attach => attach.attachId !== individual.relatedPhotoFaceId);

    if (!attachs.length) {
        return <div className={"text-[12px]"}>Anexos: <span className={"text-[#676a6c]! font-bold! info"}></span></div>
    }

    return (
        <>
            <div className={"text-[12px]"}>Anexos: </div>

            <div className="grid h-30.75 grid-cols-3 gap-2 overflow-y-auto pr-1">
                {attachs.filter(a => a.extension !== "application/pdf").map((attach) => (
                    <div key={attach.attachId} className="h-16 overflow-hidden rounded border border-gray-200 bg-gray-100" title={attach.description ?? ""}>
                        <img className="h-full w-full object-contain" src={getAttachUrl(attach.attachId)} alt={attach.description ?? "Anexo"}/>
                    </div>
                ))}
            </div>
        </>
    );
}

const IndividualInfo = ({individual, print = false}) => {
    const cpf = projectUtils.formatCpfToView(individual.cpf);
    const rg = [individual.rg, individual.emitterRg].filter(Boolean).join(" ");
    const birth = formatBirth(individual);
    const address = formatAddress(individual);
    const family = [individual.mother, individual.father].filter(Boolean).join(" e ");
    const hasAssociations = individual.hasoccurrence || individual.hasencounter || individual.hassos || individual.hasbos;
    const contentClassName = print ? "space-y-1 text-sm" : "space-y-1 text-sm resume-content";

    const {data: boName} = useQuery({
        queryKey: ['boName'],
        queryFn: () => bosService.getBoName(),
        initialData: 'BO'
    });

    return (
        <div className={contentClassName}>
            <div className={"flex items-start gap-2"}>
                <IndividualSituationPrint situation={individual.situation}/>
                <h3 className={`${print ? "" : "truncate"} text-lg font-bold`} title={individual.name}>{individual.name ?? ""}</h3>
            </div>

            <div className={"text-[12px]"}>CPF: <span className={"text-[#676a6c]! font-bold! info"}>{cpf}</span></div>
            <div className={"text-[12px]"}>RG: <span className={"text-[#676a6c]! font-bold! info"}>{rg}</span></div>
            <div className={"text-[12px]"}>Data de nascimento: <span className={"text-[#676a6c]! font-bold! info"}>{birth}</span></div>
            <span className={`${print ? "" : "truncate"} line-clamp-2 text-[12px]`} title={address}>Endereço: <span className={"text-[#676a6c]! font-bold! info"}>{address}</span></span>
            <span className={`${print ? "" : "truncate"} line-clamp-2 text-[12px]`} title={family}>Pai/Mãe: <span className={"text-[#676a6c]! font-bold! info"}>{family}</span></span>

            {hasAssociations && (
                <div className={"text-[12px] flex items-center gap-1"}>
                    <span>Associações:</span>
                    <span className={"text-[#676a6c]! font-bold! info flex items-center"}>
                        {individual.hasoccurrence && <img title={`Associado à ${individual.hasoccurrence} ocorrência(s)`} className={"w-5.25"} src={getAssetPath(`img/dashboard-svg/sw.occurrence.svg`)} alt="image"/>}
                        {individual.hasencounter && <img title={`Associado à ${individual.hasencounter} abordagem(s)`} className={"w-5.25"} src={getAssetPath(`/img/dashboard-svg/sw.approach.svg`)} alt="image"/>}
                        {individual.hassos && <img title={`Associado à ${individual.hassos} SOS(s)`} className={"w-5.25 h-5"} src={getAssetPath(`/img/dashboard-svg/sw.sos.svg`)} alt="image"/>}
                        {individual.hasbos && <img title={`Associado à ${individual.hasbos} ${boName}(s)`} className={"w-5.25"} src={getAssetPath(`/img/dashboard-svg/sw.bogcm.svg`)} alt="image"/>}
                    </span>
                </div>
            )}
        </div>
    );
}

const IndividualCard = ({individual, navigate, imageMode}) => {
    const data = getIndividualJsonData(individual);
    const cardData = {...individual, ...data, cpf: individual.cpf ?? data.cpf};

    return (
        <div className="individual-card col-span-12 sm:col-span-4 lg:col-span-3">
            <div
                className="relative h-full w-full cursor-pointer rounded border border-gray-200 bg-white p-3 text-left shadow-sm transition hover:border-gray-300 hover:shadow"
                onClick={() => openIndividual(cardData.cpf, navigate)}
            >
                <IndividualPhoto individual={cardData}/>

                <div className="mt-3 rounded border border-gray-100 bg-white p-3 resume-content">
                    <IndividualInfo individual={cardData}/>
                    {imageMode  && <IndividualAttachs individual={cardData}/> }
                </div>
            </div>
        </div>
    );
}

const IndividualCardPrint = ({individual}) => {
    const data = getIndividualJsonData(individual);
    const cardData = {...individual, ...data, cpf: individual.cpf ?? data.cpf};

    return (
        <div className="h-full w-full break-inside-avoid [page-break-inside:avoid]">
            <div className="flex h-full rounded border border-gray-200 bg-white p-3 text-left shadow-sm relative">
                <div className="rounded border border-gray-100 bg-white p-2 w-full">
                    <IndividualInfo individual={cardData} print={true}/>
                </div>
                <div className={"w-50"}>
                    <IndividualPhoto individual={cardData} fullHeight={true}/>
                </div>
            </div>
        </div>
    );
}

const IndividualSituationPrint = ({situation}) => {
    if (!situation) return null;

    return (
        <div className={`rounded px-2 py-1 text-center text-xs font-bold text-white ${situationClass[situation]}`}>
            {situation}
        </div>
    );
}

const IndividualCards = ({
    rowData = [],
    printMode = false,
    imageMode = null,
    hasSearched = false,
    currentPage: controlledCurrentPage,
    pageSize: controlledPageSize,
    onCurrentPageChange,
    onPageSizeChange,
}) => {
    const navigate = navigateHook();
    const [internalCurrentPage, setInternalCurrentPage] = useState(1);
    const [internalPageSize, setInternalPageSize] = useState(() => getIndividualDefaultPageSize("cards"));

    const currentPage = controlledCurrentPage ?? internalCurrentPage;
    const pageSize = controlledPageSize ?? internalPageSize;
    const setCurrentPage = onCurrentPageChange ?? setInternalCurrentPage;
    const setPageSize = onPageSizeChange ?? setInternalPageSize;

    const paginatedRowData = useMemo(() => {
        return getIndividualPaginatedRowData(rowData, currentPage, pageSize);
    }, [currentPage, pageSize, rowData]);

    if (printMode) {
        return (
            <div className={"flex flex-wrap items-stretch gap-x-2 gap-y-3"}>
                {
                    rowData.map((individual, index) => {

                        return (
                            <div className={"flex w-[49%] shrink-0"} key={index}>
                                <IndividualCardPrint key={index} individual={individual}/>

                            </div>
                        )
                    })
                }
            </div>
        )
    }

    return (
        <>
            <div className={"w-full flex justify-end"}>
                <IndividualGridPagination
                    rowData={rowData}
                    currentPage={currentPage}
                    pageSize={pageSize}
                    modeVisualization="cards"
                    onCurrentPageChange={setCurrentPage}
                    onPageSizeChange={setPageSize}
                />
            </div>

            {
                !rowData.length ? (
                    <div className="rounded border border-blue-200 bg-blue-50 p-1 px-2 text-sm text-blue-900 absolute top-3.5 w-[calc(100vw-800px)]">
                        {hasSearched ? "Nenhum registro encontrado." : "* Preencha um dos filtros antes de realizar a pesquisa."}
                    </div>
                ) : (
                    <div className="grid grid-cols-12 gap-3 max-h-[calc(100vh-191px)] overflow-y-auto">
                        {
                            paginatedRowData.map((individual, index) => (<IndividualCard key={index} imageMode={imageMode} individual={individual} navigate={navigate}/>))
                        }
                    </div>
                )
            }
        </>
    );
};

export default IndividualCards;
